import { colors } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { Tabs } from '@creditornot/cb-tabs';
import { LocationDescriptorObject } from 'history';
import { useMemo } from 'react';
import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { useInsightSubRoutes } from '../hooks/useInsightsSubRoutes';

enum InsightsTab {
  OVERVIEW = 'OVERVIEW',
  PERFORMANCE = 'PERFORMANCE',
  OPERATIONS = 'OPERATIONS',
  CUSTOMER_BASE = 'CUSTOMER_BASE',
  MENU = 'MENU',
  RATINGS_AND_REVIEWS = 'RATINGS_AND_REVIEWS',
}

const InsightsNavigationContainer = styled.div`
  border-block-end: 1px solid ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
`;

export const InsightsNavigation = () => {
  const { formatMessage } = useIntl();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { indexLink, performanceLink, operationsLink, customerBaseLink, menuLink, ratingsAndReviewsLink } =
    useInsightSubRoutes();

  const linkMapping: Record<InsightsTab, LocationDescriptorObject> = useMemo(
    () => ({
      [InsightsTab.PERFORMANCE]: performanceLink(history.location),
      [InsightsTab.OPERATIONS]: operationsLink(history.location),
      [InsightsTab.CUSTOMER_BASE]: customerBaseLink(history.location),
      [InsightsTab.MENU]: menuLink(history.location),
      [InsightsTab.RATINGS_AND_REVIEWS]: ratingsAndReviewsLink(history.location),
      // Keep the overview as the last one to so other pages gets checked first
      [InsightsTab.OVERVIEW]: indexLink(history.location),
    }),
    [indexLink, performanceLink, operationsLink, customerBaseLink, menuLink, ratingsAndReviewsLink, history.location],
  );

  const activeTab = useMemo(() => {
    return (
      (Object.entries(linkMapping).find(
        ([_, location]) => location.pathname && url.startsWith(location.pathname),
      )?.[0] as InsightsTab) ?? InsightsTab.OVERVIEW
    );
  }, [url, linkMapping]);

  const handleTabChange = (tabId: InsightsTab) => {
    history.push(linkMapping[tabId]);
  };

  return (
    <InsightsNavigationContainer>
      <Tabs<InsightsTab>
        activeTabId={activeTab}
        items={[
          { id: InsightsTab.OVERVIEW, label: formatMessage({ id: 'insights.overview' }) },
          { id: InsightsTab.PERFORMANCE, label: formatMessage({ id: 'insights.performance.header' }) },
          { id: InsightsTab.OPERATIONS, label: formatMessage({ id: 'insights.operations.header' }) },
          { id: InsightsTab.CUSTOMER_BASE, label: formatMessage({ id: 'insights.customer-base.header' }) },
          { id: InsightsTab.MENU, label: formatMessage({ id: 'insights.menu.header' }) },
          { id: InsightsTab.RATINGS_AND_REVIEWS, label: formatMessage({ id: 'insights.ratings-and-reviews.header' }) },
        ]}
        onChange={handleTabChange}
      />
    </InsightsNavigationContainer>
  );
};
